import React, { Suspense, useLayoutEffect, useState, useRef, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, X, BookOpen } from 'lucide-react';
import { CopperOxideScene3D } from './lab/CopperOxideScene3D';
import { CopperOxideSidebar } from './panels/CopperOxideSidebar';
import { CameraController } from './lab/CameraController';
import CopperOxideToolbar from './lab/CopperOxideToolbar';
import LoaderOverlay from './lab/LoaderOverlay';
import { useCopperOxideStore } from './store/useCopperOxideStore';
import { ThemeManager } from '../titration/lib/ThemeManager';
import { useCurrentUser } from '../../AuthComponents';
import {
  useExperiment,
  useStudentProgress,
  usePreLabQuestions,
  useDiscussionQuestions,
  useSubmitPreLabAssessment,
  useTeacherBypass,
  useSaveLabData,
} from '../../../utils/convexHooks';
import { Id } from '../../../convex/_generated/dataModel';
import PreLabAssessment from '../../PreLabAssessment';

function WebGLStateTracker({ onReady }: { onReady: () => void }) {
  const frameCount = useRef(0);
  useFrame(() => {
    frameCount.current += 1;
    if (frameCount.current === 2) onReady();
  });
  return null;
}

function TutorialOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative max-w-xl w-[92vw] bg-card text-foreground rounded-2xl shadow-2xl border border-border p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted/70 text-muted-foreground hover:text-foreground transition"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-rose-500/20 border border-rose-500/30 flex items-center justify-center">
            <BookOpen className="w-5 h-5 text-rose-500" />
          </div>
          <div>
            <h2 className="text-xl font-bold bg-gradient-to-r from-amber-500 to-rose-500 bg-clip-text text-transparent">
              Empirical Formula of Copper(II) Oxide
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">Activity 3.13 — reduction of CuO by hydrogen gas</p>
          </div>
        </div>

        <div className="space-y-3 text-sm text-muted-foreground">
          <div className="rounded-lg bg-muted/50 border border-border p-3">
            <p className="text-xs font-bold uppercase tracking-widest text-rose-500 mb-1">Aim</p>
            <p className="text-foreground">Determine the empirical formula of copper(II) oxide by reducing it with hydrogen gas and measuring the mass difference.</p>
          </div>

          <p className="text-xs font-bold uppercase tracking-widest text-rose-500 mt-4">Procedure for each trial</p>
          <ol className="list-decimal list-inside space-y-1.5 ml-2 text-foreground">
            <li>Weigh the empty 12 cm glass tube.</li>
            <li>Add CuO powder to the middle of the tube; weigh again.</li>
            <li>Set up the gas generator (Zn + HCl) and the water safety bottle.</li>
            <li>Connect the apparatus with rubber tubing.</li>
            <li>Start H₂ flow and wait <strong>10 s</strong> before heating.</li>
            <li>Light the spirit lamp. CuO turns from black to copper-brown.</li>
            <li>Stop heating, but keep H₂ flowing while the tube cools.</li>
            <li>Dab the tube end with a cotton bud to remove water.</li>
            <li>Weigh again; reheat until the mass is constant.</li>
            <li>Press <em>Record &amp; Calculate</em> to compute the empirical formula.</li>
          </ol>
          <p>Repeat for the three trials (0.8 g, 1.6 g, 2.4 g of CuO). Compare ratios in Data and Chart.</p>

          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial 1</p>
              <p className="text-lg font-bold text-emerald-500">0.8 g</p>
              <p className="text-[11px] text-muted-foreground">CuO</p>
            </div>
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial 2</p>
              <p className="text-lg font-bold text-amber-500">1.6 g</p>
              <p className="text-[11px] text-muted-foreground">CuO</p>
            </div>
            <div className="rounded-lg bg-card border border-border p-2 text-center">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Trial 3</p>
              <p className="text-lg font-bold text-red-500">2.4 g</p>
              <p className="text-[11px] text-muted-foreground">CuO</p>
            </div>
          </div>

          <div className="pt-2 border-t border-border text-xs">
            <p>
              <strong className="text-foreground">Tip:</strong> Stopping H₂ flow while the tube is still hot lets the copper re-oxidise, perturbing the empirical formula. Wait for full cooling!
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-5 w-full py-2.5 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-medium transition shadow-md shadow-rose-500/20"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

interface BaseProps {
  experimentConfig: any;
  progress: any;
  discussionQuestions: any;
}

function CopperOxideExperimentBase({ experimentConfig }: BaseProps) {
  const resetAll = useCopperOxideStore(s => s.resetAll);
  const results = useCopperOxideStore(s => s.results);
  const showTutorial = useCopperOxideStore(s => s.showTutorial);
  const setShowTutorial = useCopperOxideStore(s => s.setShowTutorial);
  const [webGLReady, setWebGLReady] = useState(false);
  const controlsRef = useRef<any>(null);

  const { user } = useCurrentUser();
  const saveLabData = useSaveLabData();

  useLayoutEffect(() => {
    ThemeManager.getInstance().setMode(ThemeManager.getInstance().getMode());
  }, []);

  useEffect(() => {
    resetAll();
  }, [resetAll]);

  const savedRef = useRef(false);
  useEffect(() => {
    if (results.length === 3 && !savedRef.current && user && experimentConfig?._id) {
      savedRef.current = true;
      saveLabData({
        studentId: user._id,
        experimentConfigId: experimentConfig._id,
        dataPoints: results,
      }).catch((e: any) => console.error('saveLabData failed:', e));
    }
  }, [results, user, experimentConfig, saveLabData]);

  const title = experimentConfig?.title || 'Empirical Formula of Copper(II) Oxide';
  const experimentMode = experimentConfig?.experimentMode || 'practice';

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-background relative text-foreground">
      <CopperOxideToolbar title={title} experimentMode={experimentMode} />

      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden relative w-full h-full bg-background">
        <div className="flex-[2] lg:flex-[3] relative w-full h-full min-h-[50vh] lg:min-h-0 bg-background/50">
          <div className="absolute inset-0">
            <LoaderOverlay isWebGLReady={webGLReady} />
            <Canvas shadows dpr={[1, 2]} gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }} className="w-full h-full">
              <WebGLStateTracker onReady={() => setWebGLReady(true)} />
              <PerspectiveCamera makeDefault position={[0, 1.2, 4.5]} fov={45} />
              <OrbitControls ref={controlsRef} enablePan panSpeed={0.5} minDistance={0.5} maxDistance={15} target={[0, 0.2, -0.1]} />
              <CameraController controlsRef={controlsRef} />
              <ambientLight intensity={0.55} />
              <directionalLight position={[5, 8, 5]} intensity={0.85} />
              <directionalLight position={[-3, 4, -2]} intensity={0.3} color="#ffd1a3" />
              <Suspense fallback={null}>
                <CopperOxideScene3D />
              </Suspense>
            </Canvas>
          </div>
        </div>
        <CopperOxideSidebar />
      </div>

      {showTutorial && <TutorialOverlay onClose={() => setShowTutorial(false)} />}
    </div>
  );
}

export default function CopperOxideExperimentWrapper() {
  const [searchParams] = useSearchParams();
  const idStr = searchParams.get('id');
  const id = idStr ? (idStr as Id<'experimentConfigs'>) : undefined;

  const navigate = useNavigate();
  const { user } = useCurrentUser();

  const experimentConfig = useExperiment(id);
  const progress = useStudentProgress(user?._id, id);
  const preLabQuestions = usePreLabQuestions(id);
  const discussionQuestions = useDiscussionQuestions(id);
  const submitPreLab = useSubmitPreLabAssessment();
  const teacherBypass = useTeacherBypass();

  if (!id) {
    return <CopperOxideExperimentBase experimentConfig={null} progress={null} discussionQuestions={null} />;
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <p className="text-xl">Please sign in to start the experiment.</p>
        <button onClick={() => navigate('/sign-in')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Sign in</button>
      </div>
    );
  }

  if (experimentConfig === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <p className="text-xl text-red-500">Experiment not found</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back to Dashboard</button>
      </div>
    );
  }

  if (
    experimentConfig === undefined ||
    progress === undefined ||
    (experimentConfig.preLab.enabled && preLabQuestions === undefined)
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
        <Loader2 className="w-12 h-12 animate-spin text-rose-500 mb-4" />
        <p className="text-sm text-muted-foreground tracking-wider uppercase">Preparing the lab…</p>
      </div>
    );
  }

  const needsPreLab =
    experimentConfig.preLab.enabled &&
    progress !== null &&
    (progress.status === 'not-started' || progress.status === 'pre-lab') &&
    (!progress.preLab || !progress.preLab.passed);

  if (needsPreLab && preLabQuestions && preLabQuestions.length > 0) {
    return (
      <div className="min-h-screen bg-background py-8 px-4 text-foreground">
        <button
          onClick={() => navigate('/student/dashboard')}
          className="mb-6 flex flex-row gap-2 items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-5 h-5" /> Back to Dashboard
        </button>
        <PreLabAssessment
          questions={preLabQuestions.map((q: any) => ({ ...q, id: q._id as string }))}
          passingScore={experimentConfig.preLab.passingScore}
          maxAttempts={experimentConfig.preLab.maxAttempts}
          showAnswersAfterFail={experimentConfig.preLab.showAnswersAfterFail}
          allowTeacherBypass={experimentConfig.allowTeacherBypass}
          onPass={async (answers) => {
            try {
              await submitPreLab({
                experimentConfigId: id,
                studentId: user._id,
                answers: answers.map((a: any) => ({
                  questionId: a.questionId as Id<'preLabQuestions'>,
                  answer: a.answer,
                })),
              });
            } catch (e: any) {
              alert(e.message || 'Failed to save pre-lab progress.');
            }
          }}
          onBypass={async () => {
            try {
              await teacherBypass({ experimentConfigId: id, studentId: user._id });
            } catch (e: any) {
              alert(e.message || 'Invalid password');
            }
          }}
        />
      </div>
    );
  }

  return (
    <CopperOxideExperimentBase
      experimentConfig={experimentConfig}
      progress={progress}
      discussionQuestions={discussionQuestions}
    />
  );
}
